import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { 
  FileText, 
  CheckCircle2,
  XCircle,
  Clock,
  ChevronLeft,
  RefreshCw,
  PoundSterling,
  AlertTriangle
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  "Pending Approval": "bg-amber-100 text-amber-700",
  "Awaiting Approval": "bg-amber-100 text-amber-700",
  "Awaiting Client Approval": "bg-amber-100 text-amber-700",
  "Submitted": "bg-amber-100 text-amber-700",
  "Approved": "bg-green-100 text-green-700",
  "Rejected": "bg-red-100 text-red-700",
};

export default function ClientPortalV2QuotesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [processing, setProcessing] = useState(false);
  const navigate = useNavigate();

  const loadData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const res = await axios.get(`${API}/api/system-expansion/client-v2/quotes`, { headers });
      setData(res.data);
    } catch (err) {
      console.error("Failed to load quotes:", err);
      toast.error("Failed to load quotes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleApprove = async (quoteId) => {
    try {
      setProcessing(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(`${API}/api/system-expansion/client-v2/quotes/${quoteId}/approve`, {}, { headers });
      
      toast.success("Quote approved successfully");
      loadData();
    } catch (err) {
      console.error("Failed to approve quote:", err);
      toast.error("Failed to approve quote");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedQuote) return;
    
    try {
      setProcessing(true);
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await axios.post(
        `${API}/api/system-expansion/client-v2/quotes/${selectedQuote.quote_id}/reject`, 
        { reason: rejectReason },
        { headers }
      );
      
      toast.success("Quote rejected");
      setShowRejectDialog(false);
      setSelectedQuote(null);
      setRejectReason("");
      loadData();
    } catch (err) {
      console.error("Failed to reject quote:", err);
      toast.error("Failed to reject quote");
    } finally {
      setProcessing(false);
    }
  };

  const summary = data?.summary || {};
  const quotes = data?.rows || [];

  // Separate pending and others
  const pendingQuotes = quotes.filter(q => 
    ["Pending Approval", "Awaiting Approval", "Submitted", "Awaiting Client Approval"].includes(q.status)
  );
  const otherQuotes = quotes.filter(q => 
    !["Pending Approval", "Awaiting Approval", "Submitted", "Awaiting Client Approval"].includes(q.status)
  );

  return (
    <div className="min-h-screen bg-slate-50" data-testid="client-v2-quotes-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-700 to-teal-600 p-6 text-white">
        <div className="max-w-6xl mx-auto">
          <Button 
            variant="ghost" 
            size="sm" 
            className="text-white hover:text-white hover:bg-teal-600 mb-2"
            onClick={() => navigate('/client-v2')}
          >
            <ChevronLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
          <h1 className="text-2xl font-bold" data-testid="page-title">Quote Approval</h1>
          <p className="text-teal-100 text-sm mt-1">Review and approve submitted quotes</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="border-l-4 border-l-teal-500" data-testid="total-card">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Total Quotes</div>
              <div className="text-3xl font-bold text-teal-700">{summary.total || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-amber-500" data-testid="pending-card">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Awaiting Approval</div>
              <div className="text-3xl font-bold text-amber-600">{summary.pending || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-green-500" data-testid="approved-card">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Approved</div>
              <div className="text-3xl font-bold text-green-600">{summary.approved || 0}</div>
            </CardContent>
          </Card>
          
          <Card className="border-l-4 border-l-red-500" data-testid="rejected-card">
            <CardContent className="p-4">
              <div className="text-sm text-slate-500">Rejected</div>
              <div className="text-3xl font-bold text-red-600">{summary.rejected || 0}</div>
            </CardContent>
          </Card>
        </div>

        {/* Pending Quotes - Action Required */}
        {pendingQuotes.length > 0 && (
          <Card className="border-amber-200">
            <CardHeader className="pb-2 bg-amber-50 rounded-t-lg">
              <CardTitle className="text-base flex items-center gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4" />
                Action Required ({pendingQuotes.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="space-y-4">
                {pendingQuotes.map((quote) => (
                  <div 
                    key={quote.quote_id} 
                    className="border rounded-lg p-4 bg-white"
                    data-testid={`quote-card-${quote.quote_id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-teal-700">{quote.job_reference}</span>
                          <Badge className={STATUS_STYLES[quote.status]}>
                            <Clock className="h-3 w-3 mr-1" />
                            {quote.status}
                          </Badge>
                        </div>
                        
                        {quote.description && (
                          <p className="text-slate-600 text-sm mb-2">{quote.description}</p>
                        )}
                        
                        <div className="flex items-center gap-4 text-sm text-slate-500">
                          {quote.contractor_name && (
                            <span>Contractor: {quote.contractor_name}</span>
                          )}
                          {quote.submitted_at && (
                            <span>Submitted: {new Date(quote.submitted_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right ml-4">
                        <div className="text-2xl font-bold text-slate-900 flex items-center justify-end">
                          <PoundSterling className="h-5 w-5" />
                          {(quote.total_inc_vat || quote.total_ex_vat || 0).toLocaleString()}
                        </div>
                        <div className="text-xs text-slate-400">
                          {quote.total_inc_vat ? "inc VAT" : "ex VAT"}
                        </div>
                        
                        <div className="flex gap-2 mt-3">
                          <Button 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleApprove(quote.quote_id)}
                            disabled={processing}
                            data-testid={`approve-btn-${quote.quote_id}`}
                          >
                            <CheckCircle2 className="h-4 w-4 mr-1" />
                            Approve
                          </Button>
                          <Button 
                            size="sm"
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            onClick={() => {
                              setSelectedQuote(quote);
                              setShowRejectDialog(true);
                            }}
                            disabled={processing}
                            data-testid={`reject-btn-${quote.quote_id}`}
                          >
                            <XCircle className="h-4 w-4 mr-1" />
                            Reject
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Other Quotes */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="h-4 w-4 text-teal-600" />
                Quote History
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={loadData} data-testid="refresh-btn">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
              </div>
            ) : otherQuotes.length === 0 && pendingQuotes.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No quotes found</p>
              </div>
            ) : otherQuotes.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <p>No historical quotes</p>
              </div>
            ) : (
              <div className="space-y-3">
                {otherQuotes.map((quote) => (
                  <div 
                    key={quote.quote_id} 
                    className="border rounded-lg p-4 bg-white"
                    data-testid={`history-quote-${quote.quote_id}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          quote.status === "Approved" ? "bg-green-100" : 
                          quote.status === "Rejected" ? "bg-red-100" : "bg-slate-100"
                        }`}>
                          {quote.status === "Approved" ? (
                            <CheckCircle2 className="h-5 w-5 text-green-600" />
                          ) : quote.status === "Rejected" ? (
                            <XCircle className="h-5 w-5 text-red-600" />
                          ) : (
                            <FileText className="h-5 w-5 text-slate-600" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium">{quote.job_reference}</span>
                            <Badge className={STATUS_STYLES[quote.status] || "bg-slate-100 text-slate-700"}>
                              {quote.status}
                            </Badge>
                          </div>
                          <div className="text-xs text-slate-500">
                            {quote.contractor_name && `${quote.contractor_name} • `}
                            {quote.submitted_at && new Date(quote.submitted_at).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="font-semibold flex items-center justify-end">
                          <PoundSterling className="h-4 w-4" />
                          {(quote.total_inc_vat || quote.total_ex_vat || 0).toLocaleString()}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Quote</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this quote.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Label htmlFor="reason">Rejection Reason</Label>
            <Textarea
              id="reason"
              placeholder="Enter reason for rejection..."
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              rows={3}
              className="mt-2"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-red-600 hover:bg-red-700"
              onClick={handleReject}
              disabled={processing}
            >
              Reject Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
